<template>
  <!-- Controles -->
  <div v-if="!soloLectura" class="controles">
    <div class="control">
      <label>Periodo anterior</label>
      <select v-model="anteriorSel" class="input-select">
        <option :value="null" disabled>Selecciona un periodo</option>
        <option v-for="c in candidatosAnterior" :key="c.id" :value="c.id">
          {{ c.periodo }} — {{ c.estado }}
        </option>
      </select>
    </div>

    <div v-if="!ocultarNivel" class="control">
      <label>Nivel</label>
      <select v-model="factorSel" class="input-select" :disabled="!factores.length">
        <option :value="null">Todos los factores (resumen)</option>
        <option v-for="f in factores" :key="f.id" :value="f.id">
          {{ f.ordinal ? fmtOrdinal(f.ordinal) + ' ' : '' }}{{ f.nombre }}
        </option>
      </select>
    </div>

    <div v-if="factorSel" class="control control-btn">
      <button class="btn salir btn-sm" @click="factorSel = null">‹ Todos los factores</button>
    </div>
  </div>

  <!-- Estados -->
  <div v-if="soloLectura && !comparacion" class="info">Sin datos comparativos disponibles.</div>
  <template v-if="!soloLectura">
    <div v-if="!anteriorSel" class="aviso">
      No hay un periodo anterior de la misma área para comparar. Registra o enlaza otra ejecución.
    </div>
    <div v-else-if="comparStore.cargando" class="info">Cargando comparación…</div>
    <div v-else-if="comparStore.error" class="aviso aviso-error">{{ comparStore.error }}</div>
  </template>

  <template v-if="mostrarDatos">
    <!-- Barra: periodos + pestañas vista + toggle emparejar -->
    <div v-if="!soloLectura" class="barra-superior">
      <div class="periodos-bar">
        <span class="badge badge-actual">Actual: {{ comparacion.actual.periodo }}</span>
        <span class="badge badge-anterior">Anterior: {{ comparacion.anterior.periodo }}</span>
      </div>
      <div class="acciones-derecha">
        <div class="vista-tabs">
          <button class="vtab" :class="{ activo: vista === 'tabla' }" @click="vista = 'tabla'; emit('update:vistaInicial', 'tabla')">Tabla</button>
          <button class="vtab" :class="{ activo: vista === 'grafico' }" @click="vista = 'grafico'; emit('update:vistaInicial', 'grafico')">Gráfico</button>
        </div>
        <button
          v-if="vista === 'tabla'"
          class="btn btn-sm"
          :class="modoEmparejar ? 'emparejar-on' : 'emparejar-off'"
          @click="toggleEmparejar"
        >
          {{ modoEmparejar ? '✓ Emparejando' : 'Emparejar manual' }}
        </button>
      </div>
    </div>

    <p v-if="modoEmparejar && vista === 'tabla' && !soloLectura" class="ayuda-emparejar">
      Elige, por cada componente, con cuál del periodo anterior se compara (útil cuando cambió el nombre).
    </p>

    <!-- ═══ TABLA ═══ -->
    <div v-show="vista === 'tabla'" class="contenedor-tabla">
      <table class="lista-tabla comparativo">
        <thead>
          <tr>
            <th>{{ factorSel ? 'Característica' : 'Factor' }}</th>
            <th v-if="modoEmparejar">Emparejar con (anterior)</th>
            <th>Grado de cumplimiento</th>
            <th class="col-num">Calif. {{ comparacion.actual.periodo }}</th>
            <th class="col-num">%</th>
            <th class="col-num">Calif. {{ comparacion.anterior.periodo }}</th>
            <th class="col-num">%</th>
            <th class="col-num">Variación</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="f in filasVisibles"
            :key="f.id_componente"
            :class="{ clickable: !factorSel && !modoEmparejar && !soloLectura }"
            @click="(!factorSel && !modoEmparejar && !soloLectura) && drill(f.id_componente)"
          >
            <td class="col-nombre">
              <span v-if="f.ordinal" class="ordinal">{{ fmtOrdinal(f.ordinal) }}</span>
              {{ f.nombre }}
              <span v-if="!factorSel && !modoEmparejar && !soloLectura" class="drill-hint">ver características ›</span>
            </td>
            <td v-if="modoEmparejar" class="col-pareo" @click.stop>
              <select
                class="input-select select-pareo"
                :disabled="guardandoPareo === f.id_componente"
                :value="f.anterior_componente?.id_componente ?? ''"
                @change="onCambiarPareo(f, $event)"
              >
                <option value="">— Automático —</option>
                <option v-for="c in componentesAnteriores" :key="c.id_componente" :value="c.id_componente">
                  {{ (c.ordinal ? fmtOrdinal(c.ordinal) + ' · ' : '') + c.nombre }}
                </option>
              </select>
            </td>
            <td>
              <span v-if="f.grado" class="badge-grado" :class="nivelClase(f.actual.calif)">{{ f.grado }}</span>
              <span v-else class="vacio">—</span>
            </td>
            <td class="col-num">
              <template v-if="!soloLectura">
                <div v-if="f.actual.calif !== null" class="databar-wrap">
                  <div class="databar" :class="nivelClase(f.actual.calif)" :style="{ width: barWidth(f.actual.pct) }"></div>
                  <span class="databar-val">{{ fmtCalif(f.actual.calif) }}</span>
                </div>
                <span v-else class="vacio">—</span>
              </template>
              <span v-else :class="nivelClase(f.actual.calif)">{{ fmtCalif(f.actual.calif) }}</span>
            </td>
            <td class="col-num">{{ fmtPct(f.actual.pct) }}</td>
            <td class="col-num">
              <template v-if="!soloLectura">
                <template v-if="f.anterior">
                  <div v-if="f.anterior.calif !== null" class="databar-wrap">
                    <div class="databar databar-tenue" :style="{ width: barWidth(f.anterior.pct) }"></div>
                    <span class="databar-val">{{ fmtCalif(f.anterior.calif) }}</span>
                  </div>
                  <span v-else class="vacio">—</span>
                </template>
                <span v-else class="sin-equiv" title="Sin equivalente en el periodo anterior (empareja manualmente)">sin equiv.</span>
              </template>
              <span v-else>{{ f.anterior ? fmtCalif(f.anterior.calif) : '—' }}</span>
            </td>
            <td class="col-num">{{ f.anterior ? fmtPct(f.anterior.pct) : '—' }}</td>
            <td class="col-num">
              <span v-if="f.variacion !== null" class="variacion" :class="varClase(f.variacion)">
                {{ varFlecha(f.variacion) }} {{ (f.variacion > 0 ? '+' : '') + f.variacion.toFixed(1) }}
              </span>
              <span v-else class="vacio">—</span>
            </td>
          </tr>

          <tr v-if="!filasVisibles.length">
            <td :colspan="modoEmparejar ? 8 : 7" class="mensaje-vacio">No hay datos consolidados para comparar en este nivel.</td>
          </tr>
        </tbody>

        <tfoot v-if="comparacion.global_factor">
          <tr class="fila-global">
            <td class="col-nombre">CALIFICACIÓN GLOBAL {{ factorSel ? 'DEL FACTOR' : 'DEL MODELO' }}</td>
            <td v-if="modoEmparejar"></td>
            <td>
              <span v-if="comparacion.global_factor.grado" class="badge-grado" :class="nivelClase(comparacion.global_factor.actual.calif)">
                {{ comparacion.global_factor.grado }}
              </span>
              <span v-else class="vacio">—</span>
            </td>
            <td class="col-num">{{ fmtCalif(comparacion.global_factor.actual.calif) }}</td>
            <td class="col-num">{{ fmtPct(comparacion.global_factor.actual.pct) }}</td>
            <td class="col-num">{{ comparacion.global_factor.anterior ? fmtCalif(comparacion.global_factor.anterior.calif) : '—' }}</td>
            <td class="col-num">{{ comparacion.global_factor.anterior ? fmtPct(comparacion.global_factor.anterior.pct) : '—' }}</td>
            <td class="col-num">
              <span v-if="comparacion.global_factor.variacion !== null" class="variacion" :class="varClase(comparacion.global_factor.variacion)">
                {{ varFlecha(comparacion.global_factor.variacion) }}
                {{ (comparacion.global_factor.variacion > 0 ? '+' : '') + comparacion.global_factor.variacion.toFixed(1) }}
              </span>
              <span v-else class="vacio">—</span>
            </td>
          </tr>
        </tfoot>
      </table>
      <p v-if="!soloLectura" class="fuente">La variación se expresa en puntos porcentuales (% actual − % anterior).</p>
    </div>

    <!-- ═══ GRÁFICO ═══ -->
    <div v-show="vista === 'grafico'" class="grafico-comp">
      <div class="leyenda">
        <span class="leyenda-item"><span class="cuadro cuadro-actual"></span> {{ comparacion.actual.periodo }} (actual)</span>
        <span class="leyenda-item"><span class="cuadro cuadro-anterior"></span> {{ comparacion.anterior.periodo }} (anterior)</span>
        <span class="grafico-nivel">{{ factorSel ? 'Por característica' : 'Por factor' }}</span>
      </div>

      <div v-if="!barras.length" class="mensaje-vacio">No hay datos para graficar.</div>
      <div v-else class="contenedor-tabla">
        <svg :viewBox="`0 0 ${anchoSvg} 240`" class="grafico-svg" preserveAspectRatio="xMidYMid meet">
          <line x1="42" y1="12" x2="42" y2="190" stroke="#ccc" />
          <line x1="42" y1="190" :x2="anchoSvg - 8" y2="190" stroke="#ccc" />
          <template v-for="t in ejeTicks" :key="'t' + t">
            <line x1="38" :y1="yPos(t)" x2="42" :y2="yPos(t)" stroke="#bbb" />
            <line x1="42" :y1="yPos(t)" :x2="anchoSvg - 8" :y2="yPos(t)" stroke="#f0f0f0" />
            <text x="34" :y="yPos(t) + 3" text-anchor="end" class="eje-label">{{ t }}</text>
          </template>
          <g v-for="(b, i) in barras" :key="b.id">
            <rect :x="grupoX(i)" :y="yPos(b.actual ?? 0)" :width="anchoBarra" :height="190 - yPos(b.actual ?? 0)" fill="#0e69a0" rx="2" />
            <text v-if="b.actual !== null" :x="grupoX(i) + anchoBarra / 2" :y="yPos(b.actual) - 3" text-anchor="middle" class="barra-val">{{ b.actual.toFixed(2) }}</text>
            <rect :x="grupoX(i) + anchoBarra + 4" :y="yPos(b.anterior ?? 0)" :width="anchoBarra" :height="190 - yPos(b.anterior ?? 0)" fill="#eea222" rx="2" />
            <text v-if="b.anterior !== null" :x="grupoX(i) + anchoBarra * 1.5 + 4" :y="yPos(b.anterior) - 3" text-anchor="middle" class="barra-val">{{ b.anterior.toFixed(2) }}</text>
            <text :x="grupoX(i) + anchoBarra + 2" y="204" text-anchor="middle" class="barra-label">{{ fmtOrdinal(b.ordinal) || b.nombre.slice(0, 10) }}</text>
          </g>
        </svg>
      </div>
      <p v-if="!soloLectura" class="fuente">Barras: calificación (0–{{ comparacion.escala_max }}) por periodo. Azul = actual, dorado = anterior.</p>
    </div>
  </template>

  <MensajeFlotante :visible="msgVisible" :mensaje="msgTexto" :tipo="msgTipo" @cerrar="msgVisible = false" />
</template>

<script setup lang="ts">
/**
 * Sección de un informe comparativo entre periodos.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, computed, onMounted, watch } from 'vue'
import { useGestionComparacion } from '@/store/GestionComparacion'
import { useGestionProcesos } from '@/store/GestionProcesoAutoevaluacion'
import { getProcesoByIdService } from '@/services/ProcesoAutoevaluacionService'
import {
  emparejarComponentesService,
  desemparejarComponenteService,
  getComponentesVersionService,
  type ComponentePlano
} from '@/services/ComparacionService'
import type { Proceso } from '@/interfaces/ProcesoAutoevaluacion'
import type { FilaComparacion } from '@/interfaces/ComparacionCalificacion'
import MensajeFlotante from '@/components/MensajeFlotante.vue'

const props = defineProps<{
  idEjecucion: number
  vistaInicial?: 'tabla' | 'grafico'
  filtroComponentes?: number[]
  soloLectura?: boolean
  ocultarNivel?: boolean
}>()

const emit = defineEmits<{ (e: 'update:vistaInicial', v: 'tabla' | 'grafico'): void }>()

function fmtOrdinal(ordinal: string | null | undefined): string {
  if (!ordinal) return ''
  const parts = ordinal.split('.')
  return parts.length <= 1 ? ordinal : parts.slice(1).join('.')
}

function sortByOrdinal(a: string | null | undefined, b: string | null | undefined): number {
  const ap = (a ?? '').split('.').map(n => parseInt(n, 10) || 0)
  const bp = (b ?? '').split('.').map(n => parseInt(n, 10) || 0)
  for (let i = 0; i < Math.max(ap.length, bp.length); i++) {
    const d = (ap[i] ?? 0) - (bp[i] ?? 0)
    if (d !== 0) return d
  }
  return 0
}

const comparStore   = useGestionComparacion()
const procesosStore = useGestionProcesos()

const procesoActual         = ref<Proceso | null>(null)
const anteriorSel           = ref<number | null>(null)
const factorSel             = ref<number | null>(null)
const factores              = ref<{ id: number; nombre: string; ordinal: string | null }[]>([])
const listo                 = ref(false)
const vista                 = ref<'tabla' | 'grafico'>(props.vistaInicial ?? 'tabla')
const modoEmparejar         = ref(false)
const componentesAnteriores = ref<ComponentePlano[]>([])
const guardandoPareo        = ref<number | null>(null)
const msgVisible            = ref(false)
const msgTexto              = ref('')
const msgTipo               = ref<'positivo' | 'error'>('positivo')

const comparacion = computed(() => comparStore.comparacion)

const mostrarDatos = computed(() =>
  props.soloLectura
    ? !!comparacion.value
    : !!comparacion.value && !!anteriorSel.value && !comparStore.cargando && !comparStore.error
)

const candidatosAnterior = computed(() => {
  if (!procesoActual.value) return []
  const base = procesoActual.value
  return procesosStore.procesos
    .filter(p =>
      p.id !== props.idEjecucion &&
      p.ambito === base.ambito &&
      (p.id_programa ?? null) === (base.id_programa ?? null)
    )
    .sort((a, b) => (b.periodo ?? '').localeCompare(a.periodo ?? ''))
})

function toast(texto: string, tipo: 'positivo' | 'error' = 'positivo') {
  msgTexto.value  = texto
  msgTipo.value   = tipo
  msgVisible.value = true
}

async function cargar(): Promise<void> {
  if (!anteriorSel.value) { comparStore.limpiar(); return }
  await comparStore.cargar(props.idEjecucion, {
    anterior:   anteriorSel.value,
    componente: factorSel.value ?? undefined
  })
  if (!factorSel.value && comparStore.comparacion) {
    factores.value = comparStore.comparacion.filas
      .map(f => ({ id: f.id_componente, nombre: f.nombre, ordinal: f.ordinal }))
      .sort((a, b) => sortByOrdinal(a.ordinal, b.ordinal))
  }
  const idVersionAnterior = comparStore.comparacion?.anterior?.id_version
  componentesAnteriores.value = idVersionAnterior
    ? (await getComponentesVersionService(idVersionAnterior)).filter(c => c.nivel > 1)
    : []
}

function drill(idComponente: number): void {
  factorSel.value = idComponente
}

function toggleEmparejar(): void {
  modoEmparejar.value = !modoEmparejar.value
  if (modoEmparejar.value && !componentesAnteriores.value.length) {
    toast('No se pudo cargar la estructura del periodo anterior.', 'error')
  }
}

async function onCambiarPareo(fila: FilaComparacion, ev: Event): Promise<void> {
  const val = (ev.target as HTMLSelectElement).value
  guardandoPareo.value = fila.id_componente
  const res = val
    ? await emparejarComponentesService(fila.id_componente, Number(val))
    : await desemparejarComponenteService(fila.id_componente)
  guardandoPareo.value = null
  if (res.ok) {
    toast(val ? 'Emparejamiento guardado.' : 'Emparejamiento automático restaurado.')
    await cargar()
  } else {
    toast(res.error ?? 'No se pudo guardar.', 'error')
  }
}

function fmtCalif(v: number | null): string { return v === null ? '—' : v.toFixed(2) }
function fmtPct(v: number | null): string   { return v === null ? '—' : `${v.toFixed(1)}%` }
function barWidth(pct: number | null): string { return `${Math.max(0, Math.min(100, pct ?? 0))}%` }

function nivelClase(calif: number | null): string {
  if (calif === null || !comparacion.value) return ''
  const escala = comparacion.value.escala_max || 5
  const norm   = escala > 0 ? (calif / escala) * 5 : calif
  if (norm >= 4.5) return 'nivel-excelente'
  if (norm >= 4.0) return 'nivel-alto'
  if (norm >= 3.0) return 'nivel-medio'
  return 'nivel-bajo'
}

function varClase(v: number): string  { return v > 0 ? 'var-pos' : v < 0 ? 'var-neg' : 'var-neutra' }
function varFlecha(v: number): string { return v > 0 ? '▲' : v < 0 ? '▼' : '—' }

const filasVisibles = computed(() => {
  const filas = comparacion.value?.filas ?? []
  const filtered = props.filtroComponentes?.length
    ? filas.filter(f => props.filtroComponentes!.includes(f.id_componente))
    : filas
  return [...filtered].sort((a, b) => sortByOrdinal(a.ordinal, b.ordinal))
})

const barras = computed(() =>
  filasVisibles.value.map(f => ({
    id:       f.id_componente,
    ordinal:  f.ordinal,
    nombre:   f.nombre,
    actual:   f.actual.calif,
    anterior: f.anterior?.calif ?? null
  }))
)

const anchoBarra = 20
const pasoGrupo  = 62
const anchoSvg   = computed(() => Math.max(300, 60 + barras.value.length * pasoGrupo))
function grupoX(i: number): number { return 56 + i * pasoGrupo }

const escalaMax = computed(() => comparacion.value?.escala_max || 5)
const ejeTicks  = computed(() => {
  const max  = Math.ceil(escalaMax.value)
  const paso = max <= 5 ? 1 : 2
  const ticks: number[] = []
  for (let t = 0; t <= max; t += paso) ticks.push(t)
  return ticks
})
function yPos(v: number): number {
  const max = escalaMax.value || 5
  return 190 - (Math.max(0, Math.min(max, v)) / max) * 178
}

watch([anteriorSel, factorSel], () => { if (listo.value && !props.soloLectura) cargar() })
watch(() => props.vistaInicial, v => { if (v) vista.value = v })

onMounted(async () => {
  if (props.soloLectura) {
    listo.value = true
    return
  }
  const [proceso] = await Promise.all([
    getProcesoByIdService(props.idEjecucion),
    procesosStore.cargarTodos()
  ])
  procesoActual.value = proceso

  const enlazada = proceso?.id_ejecucion_anterior ?? proceso?.anterior?.id_ejecucion ?? null
  anteriorSel.value = enlazada ?? candidatosAnterior.value[0]?.id ?? null

  listo.value = true
  await cargar()
})
</script>

<style scoped>
.controles { display: flex; flex-wrap: wrap; gap: 1.25rem; align-items: flex-end; margin-bottom: 1.25rem; }
.control { display: flex; flex-direction: column; gap: 0.35rem; }
.control > label { font-size: 0.8rem; font-weight: 600; color: #555; }
.control-btn { justify-content: flex-end; }
.input-select {
  padding: 0.5rem 0.75rem; border: 1px solid #e6e5e5; border-radius: var(--radius-md);
  font-size: 0.9rem; font-family: var(--font-family); background-color: #f9f9f9; min-width: 200px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.input-select:focus { outline: none; border-color: #0e69a0; box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1); }
.input-select:disabled { opacity: 0.6; }
.select-pareo { min-width: 200px; }

.aviso { padding: 0.85rem 1rem; background: #fff3cd; color: #856404; border-radius: var(--radius-md); font-size: 0.88rem; }
.aviso-error { background: #f8d7da; color: #721c24; }
.info { padding: 1rem 0; color: #555; }

.barra-superior { display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
.periodos-bar   { display: flex; gap: 0.5rem; }
.badge { display: inline-block; padding: 4px 10px; border-radius: 12px; font-weight: 600; font-size: 0.8rem; }
.badge-actual   { background-color: #e8f4fd; color: #0e69a0; }
.badge-anterior { background-color: #fdf1dc; color: #8a6100; }
.acciones-derecha { display: flex; align-items: center; gap: 0.75rem; }
.vista-tabs { display: inline-flex; border: 1px solid #cfe0ee; border-radius: 8px; overflow: hidden; }
.vtab { padding: 0.4rem 0.9rem; border: none; background: #fff; color: #555; font-weight: 600; font-size: 0.82rem; cursor: pointer; font-family: var(--font-family); }
.vtab.activo { background: #0e69a0; color: #fff; }
.ayuda-emparejar { font-size: 0.82rem; color: #856404; background: #fff8e6; border: 1px solid #f0d98a; border-radius: 8px; padding: 0.5rem 0.85rem; margin: 0 0 0.75rem; }

.contenedor-tabla { overflow-x: auto; }
.lista-tabla { width: 100%; border-collapse: collapse; }
.lista-tabla th, .lista-tabla td { padding: 10px 14px; text-align: left; border-bottom: 1px solid #e0e0e0; font-size: 0.9rem; }
.lista-tabla thead { background-color: #f5f5f5; font-weight: 600; text-transform: uppercase; font-size: 0.72rem; letter-spacing: 0.05em; color: #555; }
.col-num    { text-align: right; white-space: nowrap; }
.col-nombre { font-weight: 500; }
.col-pareo  { min-width: 220px; }
.ordinal    { display: inline-block; min-width: 2.2em; color: #0e69a0; font-weight: 700; }
.vacio      { color: #bbb; }
.clickable  { cursor: pointer; transition: background-color 0.12s ease; }
.clickable:hover { background-color: #f4faff; }
.drill-hint { margin-left: 0.5rem; font-size: 0.72rem; color: #0e69a0; opacity: 0; transition: opacity 0.12s ease; }
.clickable:hover .drill-hint { opacity: 1; }
.badge-grado { display: inline-block; padding: 2px 9px; border-radius: 10px; background: #f0f0f0; font-size: 0.76rem; font-weight: 600; white-space: nowrap; }

.databar-wrap  { position: relative; display: flex; align-items: center; justify-content: flex-end; min-width: 90px; height: 22px; background: #f4f4f4; border-radius: 4px; overflow: hidden; }
.databar       { position: absolute; left: 0; top: 0; bottom: 0; opacity: 0.28; }
.databar-tenue { background: #9aa0a6; opacity: 0.22; }
.databar-val   { position: relative; z-index: 1; padding: 0 8px; font-weight: 600; font-size: 0.85rem; }
.sin-equiv     { font-size: 0.75rem; color: #b08900; font-style: italic; }

.variacion  { font-weight: 700; font-size: 0.85rem; white-space: nowrap; }
.var-pos    { color: #28a745; }
.var-neg    { color: #dc3545; }
.var-neutra { color: #999; }

.nivel-excelente { color: #28a745; }
.nivel-alto      { color: #17a2b8; }
.nivel-medio     { color: #d39e00; }
.nivel-bajo      { color: #dc3545; }
.databar.nivel-excelente { background: #28a745; }
.databar.nivel-alto      { background: #17a2b8; }
.databar.nivel-medio     { background: #ffc107; }
.databar.nivel-bajo      { background: #dc3545; }
.badge-grado.nivel-excelente { background: #d4edda; color: #155724; }
.badge-grado.nivel-alto      { background: #d1ecf1; color: #0c5460; }
.badge-grado.nivel-medio     { background: #fff3cd; color: #856404; }
.badge-grado.nivel-bajo      { background: #f8d7da; color: #721c24; }

.fila-global td          { background-color: #eef5fb; border-top: 2px solid #0e69a0; font-weight: 700; }
.fila-global .col-nombre { color: #0e69a0; text-transform: uppercase; font-size: 0.82rem; letter-spacing: 0.03em; }

.mensaje-vacio { color: #888; font-style: italic; text-align: center; padding: 1.25rem 0; }
.fuente        { font-size: 0.75rem; color: #999; margin: 0.75rem 0 0; font-style: italic; }

.grafico-comp { margin-top: 0.5rem; }
.leyenda       { display: flex; align-items: center; gap: 1.25rem; margin-bottom: 0.5rem; font-size: 0.82rem; color: #555; }
.leyenda-item  { display: inline-flex; align-items: center; gap: 0.4rem; }
.cuadro        { width: 12px; height: 12px; border-radius: 3px; display: inline-block; }
.cuadro-actual   { background: #0e69a0; }
.cuadro-anterior { background: #eea222; }
.grafico-nivel { margin-left: auto; font-weight: 600; color: #0e69a0; }
.grafico-svg   { width: 100%; height: auto; min-width: 300px; }
.eje-label  { font-size: 9px; fill: #888; }
.barra-val  { font-size: 9px; font-weight: 700; fill: #333; }
.barra-label { font-size: 9px; fill: #555; }

.btn { padding: 0.6rem 1.25rem; font-weight: 600; border: none; border-radius: 8px; cursor: pointer; transition: all 0.15s ease; font-family: var(--font-family); font-size: 0.9rem; }
.btn-sm { padding: 0.4rem 0.85rem; font-size: 0.8rem; }
.btn.salir { background-color: white; border: 1px solid #ccc; color: #333; }
.btn.salir:hover { background-color: #d41a1a; color: white; }
.emparejar-off { background: #fff; border: 1px solid #0e69a0; color: #0e69a0; }
.emparejar-off:hover { background: #0e69a0; color: #fff; }
.emparejar-on  { background: #0e69a0; color: #fff; }
</style>
